/* ==========================================================================
 * juizs-collection-filter.js  —  filter + filterbalk op de collectiepagina
 * --------------------------------------------------------------------------
 * Hoort bij `sections/juizs-collection-grid.liquid`. Besluit OD-030a (29-07):
 * PROGRESSIVE ENHANCEMENT.
 *
 * Zonder dit bestand blijft de schakelaar een rij echte links naar de
 * collecties — werkende, crawlbare navigatie. Met dit bestand wordt de klik
 * onderschept en filteren we in de pagina, met de keuze in de URL zodat
 * delen, herladen en de terugknop blijven werken.
 *
 * TWEE SOORTEN TABS (golf 6c, 2026-10-01):
 * · VERSIE (`data-versie-filter="all-day"` …) kijkt naar `data-versie` op de
 *   kaart. Een kaart zonder versie-tag blijft ALTIJD zichtbaar (OD-030b).
 *   URL: `?versie=all-day` — bestaande links blijven werken.
 * · COLLECTIE (`data-versie-filter="collectie"` + `data-filter-collection`)
 *   kijkt naar `data-collections` op de kaart. Lidmaatschap is eenduidig:
 *   wie niet in de collectie zit, verdwijnt. URL: `?filter=<handle>`.
 *   Staan beide in de URL, dan wint een bekende `filter`.
 *
 * FILTERBALK (golf 6c): los van het filter houdt het script bij of de balk
 * links of rechts verder scrollt (`data-more`, voor de randhint in de
 * sectie-CSS) en haalt het de actieve tab in beeld — horizontaal, in de balk
 * zelf. Nooit scrollIntoView: die verschuift ook de pagina (Terry, 29-07).
 * Dat deel draait ook als het filter uit staat (gepagineerd raster).
 *
 * WAAROM IN DE <head> (wiki 54-patronen #15): defer-scripts draaien in
 * DOM-volgorde. Een script dat onderaan de body staat kan niet rekenen op
 * wat er middenin de pagina gebeurt. Vandaar ook het DOMContentLoaded-
 * vangnet onderaan.
 *
 * ⚠️ HARDE GRENS: de sectie zet zelf `data-filter-enabled="false"` zodra het
 * raster pagineert. Filteren we dan toch, dan verbergen we kaarten op
 * pagina 1 terwijl er meer bestaan — een stille, moeilijk vindbare fout.
 * Niet omzeilen.
 * ========================================================================== */
(function () {
  'use strict';

  var LABEL_ATTR = 'data-versie-noun';
  /* Zoveel ruimte houdt de actieve tab vrij van de rand van de balk (en dus
     van de randhint). De snappunten zelf komen uit de sectie-CSS. */
  var EDGE = 32;
  /* Elk raster met een live filter; één popstate-listener bedient ze allemaal
     (de theme-editor hertekent secties, dan stapelen listeners anders op). */
  var roots = [];

  function reduceMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* Sleutel van een tab: de versie ('alles', 'all-day', …) of 'c:<handle>'. */
  function keyOf(el) {
    var mode = el.getAttribute('data-versie-filter') || 'alles';
    if (mode !== 'collectie') return mode;
    var handle = el.getAttribute('data-filter-collection');
    return handle ? 'c:' + handle : '';
  }

  /* ── Filterbalk ─────────────────────────────────────────────────────── */

  /* data-more="start end": aan welke kant staat er nog iets? De sectie-CSS
     tekent daar de zachte rand. Passen alle tabs, dan geen attribuut. */
  function updateMore(bar) {
    var max = bar.scrollWidth - bar.clientWidth;
    var more = [];
    if (max > 1) {
      if (bar.scrollLeft > 1) more.push('start');
      if (bar.scrollLeft < max - 1) more.push('end');
    }
    var value = more.join(' ');
    if ((bar.getAttribute('data-more') || '') === value) return;
    if (value) {
      bar.setAttribute('data-more', value);
    } else {
      bar.removeAttribute('data-more');
    }
  }

  /* Positie van een tab in de scrollinhoud, gemeten vanaf de binnenrand. */
  function offsetIn(bar, el, barRect) {
    return el.getBoundingClientRect().left - barRect.left - bar.clientLeft + bar.scrollLeft;
  }

  /* Haal `tab` helemaal in beeld, met EDGE ruimte aan beide kanten. We kiezen
     een écht snappunt uit de CSS (scroll-padding + scroll-snap-align van elke
     tab), zo dicht mogelijk bij de huidige stand. Zo duwt de scroll-snap van
     de balk de tab na afloop niet terug. */
  function reveal(bar, tab, smooth) {
    if (!bar || !tab) return;
    var max = bar.scrollWidth - bar.clientWidth;
    if (max < 1) return;

    var barRect = bar.getBoundingClientRect();
    var start = offsetIn(bar, tab, barRect);
    var end = start + tab.getBoundingClientRect().width;
    var cur = bar.scrollLeft;
    /* Helemaal aan het begin of eind is de pilpadding genoeg: daar staat
       geen randhint. */
    var lo = Math.min(max, end - bar.clientWidth + EDGE);
    var hi = Math.max(0, start - EDGE);
    if (cur >= lo - 1 && cur <= hi + 1) return;

    var style = window.getComputedStyle(bar);
    var padL = parseFloat(style.scrollPaddingLeft) || 0;
    var padR = parseFloat(style.scrollPaddingRight) || 0;
    var best = null;
    var candidates = [0, max];
    Array.prototype.forEach.call(bar.querySelectorAll('a'), function (a) {
      var align = window.getComputedStyle(a).scrollSnapAlign || '';
      var s = offsetIn(bar, a, barRect);
      if (align.indexOf('end') !== -1) {
        candidates.push(s + a.getBoundingClientRect().width + padR - bar.clientWidth);
      } else if (align.indexOf('start') !== -1) {
        candidates.push(s - padL);
      }
    });
    function pick(from, to) {
      var found = null;
      candidates.forEach(function (s) {
        s = Math.max(0, Math.min(max, s));
        if (s < from - 1 || s > to + 1) return;
        if (found === null || Math.abs(s - cur) < Math.abs(found - cur)) found = s;
      });
      return found;
    }
    best = pick(lo, hi);
    /* Geen snappunt met EDGE ruimte: dan een snappunt waarbij de tab nog
       helemaal in de pil staat, en anders zijn eigen begin. */
    if (best === null) best = pick(Math.min(max, end - bar.clientWidth + padR), Math.max(0, start - padL));
    if (best === null) best = Math.max(0, Math.min(max, start - padL));

    if (typeof bar.scrollTo === 'function') {
      bar.scrollTo({ left: best, behavior: smooth && !reduceMotion() ? 'smooth' : 'auto' });
    } else {
      bar.scrollLeft = best;
    }
    /* Een directe scroll staat er meteen; het scroll-event komt pas bij het
       volgende frame (in een achtergrondtabblad helemaal niet). Bij vloeiend
       scrollen werken de scroll-events de rand onderweg bij. */
    updateMore(bar);
  }

  function initBar(bar) {
    if (bar.dataset.barReady === 'true') return;
    bar.dataset.barReady = 'true';

    function update() { updateMore(bar); }

    bar.addEventListener('scroll', update, { passive: true });
    /* Toetsenbord: Chrome haalt een HALF zichtbare link bij focus niet zelf in
       beeld, dan viel de focusring half buiten de pil (gemeten 01-10). Eén tick
       later, zodat de eigen focus-scroll van de browser eerst klaar is.
       ALLEEN bij toetsenbordfocus (:focus-visible): een muisklik geeft de link
       al focus bij mousedown. Schoof de balk dan meteen, dan kwam mouseup op
       een andere plek en verdween de klik. Na een klik of tik haalt de
       klikhandler de tab in beeld. */
    bar.addEventListener('focusin', function (e) {
      var tab = e.target && e.target.closest ? e.target.closest('a') : null;
      if (!tab || !bar.contains(tab)) return;
      var keyboard = false;
      try { keyboard = tab.matches(':focus-visible'); } catch (err) { keyboard = false; }
      if (keyboard) window.setTimeout(function () { reveal(bar, tab, true); }, 0);
    });
    if (typeof window.ResizeObserver === 'function') {
      /* Ook de tabs: als het webfont binnenkomt, worden ze breder. */
      var ro = new window.ResizeObserver(update);
      ro.observe(bar);
      Array.prototype.forEach.call(bar.children, function (el) { ro.observe(el); });
    } else {
      window.addEventListener('resize', update);
    }
    update();
    reveal(bar, bar.querySelector('[aria-current="true"]'), false);
  }

  /* ── Filter ─────────────────────────────────────────────────────────── */

  function initRoot(root) {
    if (root.dataset.filterEnabled !== 'true') return;
    if (root.dataset.filterReady === 'true') return;

    var grid = root.querySelector('[data-versie-grid]');
    if (!grid) return;

    var bar = root.querySelector('.juizs-segbar');
    var countEl = root.querySelector('[data-versie-count]');
    var emptyEl = root.querySelector('[data-versie-empty]');
    var tabs = Array.prototype.slice.call(root.querySelectorAll('.juizs-segbar [data-versie-filter]'));
    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-versie]'));
    if (!tabs.length || !cards.length) return;

    /* Telwoord uit de gerenderde teller halen, zodat de tekst uit Liquid komt
       en niet hier hardgecodeerd staat. */
    var noun = 'producten';
    if (countEl) {
      noun = countEl.getAttribute(LABEL_ATTR) || countEl.textContent.replace(/^\s*\d+\s*/, '').trim() || noun;
    }

    function knownKey(key) {
      if (!key) return false;
      for (var i = 0; i < tabs.length; i++) {
        if (keyOf(tabs[i]) === key) return true;
      }
      return false;
    }

    /* Welke tab vraagt de URL? Een bekende `filter` wint van `versie`;
       een onbekende waarde valt terug op Alles. */
    function readKey() {
      var params = new URLSearchParams(window.location.search);
      var handle = params.get('filter');
      if (handle && knownKey('c:' + handle)) return 'c:' + handle;
      return params.get('versie') || 'alles';
    }

    function matches(card, key) {
      if (key === 'alles') return true;
      if (key.indexOf('c:') === 0) {
        /* Lidmaatschap is eenduidig: hier géén "zonder tag altijd zichtbaar". */
        var handles = (card.getAttribute('data-collections') || '').split(' ');
        return handles.indexOf(key.slice(2)) !== -1;
      }
      /* Een product zonder versie-tag heeft een lege data-versie en blijft
         altijd zichtbaar — nooit stilzwijgend wegfilteren. */
      var own = card.getAttribute('data-versie');
      return own === '' || own === key;
    }

    function apply(key, push, smooth) {
      if (!knownKey(key)) key = 'alles';

      var shown = 0;
      cards.forEach(function (card) {
        var match = matches(card, key);
        card.hidden = !match;
        if (match) shown++;
      });

      var active = null;
      tabs.forEach(function (tab) {
        if (keyOf(tab) === key) {
          tab.setAttribute('aria-current', 'true');
          active = tab;
        } else {
          tab.removeAttribute('aria-current');
        }
      });

      if (emptyEl) emptyEl.hidden = shown !== 0;
      if (countEl) {
        countEl.textContent = '';
        var strong = document.createElement('strong');
        strong.textContent = String(shown);
        countEl.appendChild(strong);
        countEl.appendChild(document.createTextNode(' ' + noun));
      }

      if (push) {
        /* Bestaande parameters BEHOUDEN. Tijdens het testen draait de pagina op
           ?view=<sjabloon>; die weggooien betekent dat een refresh terugvalt op
           het standaard collectie-sjabloon. Ook filter- en UTM-parameters van
           campagnes horen te blijven staan. Alleen onze eigen twee wisselen. */
        var params = new URLSearchParams(window.location.search);
        if (key.indexOf('c:') === 0) {
          params.delete('versie');
          params.set('filter', key.slice(2));
        } else if (key === 'alles') {
          params.delete('versie');
          params.delete('filter');
        } else {
          params.delete('filter');
          params.set('versie', key);
        }
        var qs = params.toString();
        var url = window.location.pathname + (qs ? '?' + qs : '');
        try {
          window.history.pushState({ juizsFilter: key }, '', url);
        } catch (e) {
          /* Sommige ingesloten contexten verbieden pushState. Het filter
             blijft dan werken, alleen de URL loopt niet mee. */
        }
      }

      /* De actieve tab in beeld — in de balk, niet in de pagina. */
      reveal(bar, active, smooth);
    }

    root.addEventListener('click', function (e) {
      var link = e.target.closest ? e.target.closest('[data-versie-filter]') : null;
      if (!link || !root.contains(link)) return;
      e.preventDefault();
      apply(keyOf(link), true, true);
      /* BEWUST GEEN scrollIntoView (Terry, 29-07). Die sprong zette de
         schakelaar strak tegen de bovenrand en verplaatste de pil onder de
         muis. Het raster staat toch al in beeld als je op een tab klikt —
         de bezoeker hoort te blijven waar hij is. */
    });

    roots.push({ root: root, sync: function () { apply(readKey(), false, false); } });
    root.dataset.filterReady = 'true';
    apply(readKey(), false, false);
  }

  /* Terugknop: de URL is de bron. Rasters die de editor weghaalde, vallen af. */
  window.addEventListener('popstate', function () {
    roots = roots.filter(function (r) { return document.documentElement.contains(r.root); });
    roots.forEach(function (r) { r.sync(); });
  });

  function initScope(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('[data-versiefilter] .juizs-segbar'), initBar);
    Array.prototype.forEach.call(scope.querySelectorAll('[data-versiefilter]'), initRoot);
  }

  function init() {
    initScope(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* De theme-editor hertekent een sectie zonder de pagina te herladen. */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelectorAll) initScope(e.target);
  });

  /* Editor: een gekozen tab-block in de balk in beeld halen. */
  document.addEventListener('shopify:block:select', function (e) {
    var tab = e.target;
    var bar = tab && tab.closest ? tab.closest('[data-versiefilter] .juizs-segbar') : null;
    if (bar) reveal(bar, tab, true);
  });
})();
